/*
 * Copyright (c) 2024-2025. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import React, {useEffect, useRef, useState} from "react";
import {RouteComponentProps, withRouter} from "react-router";
import {useTranslation} from "react-i18next";
import {useDispatch} from "useDispatch";
import {useTypedSelector} from "helpers/reducers";
import Loader from "components/Loader";
import clsx from "clsx";
import {UserGroupIcon} from "@heroicons/react/20/solid";
import {consoleLog, currencyNumberFormat} from "utils/utility";
import BookingPackageItemsDurations from "pages/Front/Booking/BookingPackageItems/BookingPackageItemsDurations";
import BookingPackageItemsWithKartingCount, {
    BookingPackageItemsWithKartingCountKartings,
} from "pages/Front/Booking/BookingPackageItems/BookingPackageItemsWithKartingCount";
import {DEFAULT_HALL_ID, IS_UPSELL_ALLOWED} from "helpers/config";
import {PackageCategory} from "types/enums/PackageCategory";
import BookingAction from "store/booking/BookingAction";
import {PackagesItemsPerson} from "types/entities/PackagesItems";
import {usePackageItemKartingOptions} from "utils/packageUtils";
import useNavigation, {RouteName} from "utils/hooks/useNavigation";
import useCustomTimeslotsNew from "utils/hooks/useCustomTimeslotsNew";
import {PackageItemPersonType} from "types/enums/PackageItemPersonType";

export interface BookingPackagesEventParams {
}

interface BookingPackagesEventProps extends RouteComponentProps<BookingPackagesEventParams> {
    packageCategory: PackageCategory;
}

const SellerPackagesAction = (props: BookingPackagesEventProps) => {
    const {t} = useTranslation();
    const bookingState = useTypedSelector(state => state.bookingState);
    const {historyPush} = useNavigation();
    const dispatch = useDispatch();
    const availablePackageItems = useTypedSelector(state => state.bookingState.bookingProcess.availablePackageItems);
    const filter = useTypedSelector(state => state.bookingState.bookingProcess.filter);
    const selectedTimeslot = useTypedSelector(state => state.bookingState.bookingProcess.selectedTimeslot);
    const seller_id = useTypedSelector(state => state.sessionState.session?.uid) || 1;
    const scrollRefTimeslots = useRef<HTMLDivElement>(null);
    const scrollRefCustomTimeslots = useRef<HTMLDivElement>(null);
    const scrollRefConfirmTimeslots = useRef<HTMLDivElement>(null);
    const {bookWithAutomaticTimeslot} = useCustomTimeslotsNew(() => {
    }, props.history, [
        scrollRefTimeslots,
        scrollRefCustomTimeslots,
        scrollRefConfirmTimeslots,
    ]);
    const [chosenDuration, setChosenDuration] = useState<number | undefined>(240);
    const [kartingAdults, setKartingAdults] = useState<BookingPackageItemsWithKartingCountKartings>({
        withKarting: 0,
        withoutKarting: 0,
    });

    const [kartingChildren, setKartingChildren] = useState<BookingPackageItemsWithKartingCountKartings>({
        withKarting: 0,
        withoutKarting: 0,
    });
    const personsCountAdult: number = filter.adults;
    const personsCountChild: number = filter.children;
    const [allFilled, setAllFilled] = useState<boolean>(false);
    const [totalPrice, setTotalPrice] = useState<number>(0);
    const [totalPriceAdult, setTotalPriceAdult] = useState<number>(0);
    const [totalPriceChildren, setTotalPriceChildren] = useState<number>(0);
    const [defaultPackageWithKartingAdults, setDefaultPackageWithKartingAdults] = useState<PackagesItemsPerson | null>(null);
    const [defaultPackageWithKartingChildren, setDefaultPackageWithKartingChildren] = useState<PackagesItemsPerson | null>(null);
    const [defaultPackageWithoutKartingAdults, setDefaultPackageWithoutKartingAdults] = useState<PackagesItemsPerson | null>(null);
    const [defaultPackageWithoutKartingChildren, setDefaultPackageWithoutKartingChildren] = useState<PackagesItemsPerson | null>(null);
    const {setPackageItemHandler, getLeastPossibleDuration} = usePackageItemKartingOptions();

    const onNextStepHandler = () => {
        const availablePackages = bookingState?.bookingProcess?.availablePackageItems;
        const availablePackagesHasUpsell = availablePackages ? availablePackages.upsells.length > 0 : false;
        if (IS_UPSELL_ALLOWED && availablePackagesHasUpsell) {
            return historyPush(RouteName.UpsellsNew, "Packages");
        }
        bookWithAutomaticTimeslot(null, true);
    };

    /**
     * Fetches packages based on category and time slot.
     */
    const fetchPackagesByCategoryAndTimeSlot = () => {
        dispatch(
            BookingAction.fetchPackagesByCategoryAndTimeSlotForSeller(
                {
                    adults: filter.adults,
                    children: filter.children,
                    date: filter.date,
                    package_category_name: props.packageCategory,
                    hall_id: DEFAULT_HALL_ID,
                    activity_ids: filter.activity_ids.length === 0 ? [1] : filter.activity_ids,
                    timeslot_id: selectedTimeslot ? selectedTimeslot.id : null,
                },
                seller_id,
            ),
        ).then(r => {
            consoleLog("fetchPackagesByCategoryAndTimeSlot: res", r);
        });
    };

    const setDefaultPackages = () => {
        if (availablePackageItems) {
            const packageWithKartingAdults = availablePackageItems?.packageItems?.adult?.find(item => item.duration === chosenDuration && item.karting);
            const packageWithKartingChildren = availablePackageItems?.packageItems?.child?.find(item => item.duration === chosenDuration && item.karting);

            setDefaultPackageWithKartingAdults(packageWithKartingAdults ? packageWithKartingAdults : null);
            setDefaultPackageWithKartingChildren(packageWithKartingChildren ? packageWithKartingChildren : null);

            const packageWithoutKartingAdults = availablePackageItems?.packageItems?.adult?.find(item => item.duration === chosenDuration && !item.karting);
            const packageWithoutKartingChildren = availablePackageItems?.packageItems?.child?.find(item => item.duration === chosenDuration && !item.karting);

            setDefaultPackageWithoutKartingAdults(packageWithoutKartingAdults ? packageWithoutKartingAdults : null);
            setDefaultPackageWithoutKartingChildren(packageWithoutKartingChildren ? packageWithoutKartingChildren : null);
        }
    };

    // useEffect(() => {
    //     fetchPackagesByCategoryAndTimeSlot();
    // }, [filter.adults, filter.children]);

    const setTotalPriceHandler = () => {
        if (!chosenDuration) {
            setTotalPrice(0);
            setTotalPriceAdult(0);
            setTotalPriceChildren(0);
        }

        const kartingPriceAdults = defaultPackageWithKartingAdults ? kartingAdults.withKarting * defaultPackageWithKartingAdults.price : 0;
        const kartingPriceChildren = defaultPackageWithKartingChildren ? kartingChildren.withKarting * defaultPackageWithKartingChildren.price : 0;
        const kartingPrice = kartingPriceAdults + kartingPriceChildren;

        const withoutKartingPriceAdults = defaultPackageWithoutKartingAdults ? kartingAdults.withoutKarting * defaultPackageWithoutKartingAdults.price : 0;
        const withoutKartingPriceChildren = defaultPackageWithoutKartingChildren
            ? kartingChildren.withoutKarting * defaultPackageWithoutKartingChildren.price
            : 0;
        const withoutKartingPrice = withoutKartingPriceAdults + withoutKartingPriceChildren;

        setTotalPrice(kartingPrice + withoutKartingPrice);
        setTotalPriceAdult(kartingPriceAdults + withoutKartingPriceAdults);
        setTotalPriceChildren(kartingPriceChildren + withoutKartingPriceChildren);

        setAllFilled(
            !!(
                chosenDuration &&
                personsCountAdult === kartingAdults.withoutKarting + kartingAdults.withKarting &&
                (defaultPackageWithKartingAdults || defaultPackageWithoutKartingAdults)
            ),
        );
    };

    const setSelectedPackageWithCount = () => {
        if (defaultPackageWithKartingAdults) {
            setPackageItemHandler(0, defaultPackageWithKartingAdults.id, defaultPackageWithKartingAdults.person_type, kartingAdults.withKarting);
        }
        if (defaultPackageWithoutKartingAdults) {
            setPackageItemHandler(1, defaultPackageWithoutKartingAdults.id, defaultPackageWithoutKartingAdults.person_type, kartingAdults.withoutKarting);
        }

        if (defaultPackageWithKartingChildren) {
            setPackageItemHandler(2, defaultPackageWithKartingChildren.id, defaultPackageWithKartingChildren.person_type, kartingChildren.withKarting);
        }
        if (defaultPackageWithoutKartingChildren) {
            setPackageItemHandler(3, defaultPackageWithoutKartingChildren.id, defaultPackageWithoutKartingChildren.person_type, kartingChildren.withoutKarting);
        }

        if (
            !defaultPackageWithKartingAdults &&
            !defaultPackageWithoutKartingAdults &&
            !defaultPackageWithKartingChildren &&
            !defaultPackageWithoutKartingChildren
        ) {
            dispatch(BookingAction.resetSelectedPackageItems());
        }
    };

    useEffect(() => {
        if (getLeastPossibleDuration()) {
            setChosenDuration(120);
        } else {
            setChosenDuration(240);
        }
    }, [availablePackageItems]);

    useEffect(() => {
        fetchPackagesByCategoryAndTimeSlot();
    }, [filter.adults, filter.children]);

    useEffect(() => {
        setSelectedPackageWithCount();
        setTotalPriceHandler();
    }, [defaultPackageWithKartingAdults, defaultPackageWithoutKartingAdults, kartingAdults]);

    useEffect(() => {
        setSelectedPackageWithCount();
        setTotalPriceHandler();
    }, [defaultPackageWithKartingChildren, defaultPackageWithoutKartingChildren, kartingChildren]);

    useEffect(() => {
        setDefaultPackages();
    }, [availablePackageItems, chosenDuration, kartingAdults]);

    useEffect(() => {
        setKartingAdults({withKarting: personsCountAdult, withoutKarting: 0});
    }, [personsCountAdult]);

    useEffect(() => {
        setKartingChildren({withKarting: personsCountChild, withoutKarting: 0});
    }, [personsCountChild]);

    if (!availablePackageItems) {
        return <Loader />;
    }

    return (
        <div className={"BookingPackagesEventBasic w-full"}>
            <div className={"border rounded-md lg:px-1 lg:py-1 xl:px-4 xl:py-3 bg-white shadow"}>
                <table
                    className={"PackagesPersons w-full lg:border-separate lg:border lg:border-spacing-y-2 lg:border-spacing-x-2 lg:border-white"}>
                    <colgroup className={"hidden lg:table-column-group"}>
                        <col className="w-auto" />
                        <col className="w-auto" />
                        <col className="w-auto" />
                        <col className="min-w-[110px] w-auto" />
                    </colgroup>
                    <thead className={"hidden lg:table-header-group"}>
                    <tr>
                        <th className={"text-left font-bold text-lg text-black"}></th>
                        <th className={"font-bold text-center text-lg text-black"}>{t("Options")}</th>
                        <th className={"font-bold text-center text-lg text-black"}>{t("Select visit duration")}</th>
                        <th className={"font-bold text-right text-lg text-black"}>{t("Price")}</th>
                    </tr>
                    </thead>
                    <tbody className={"flex flex-col lg:table-row-group"}>
                    <tr
                        className={
                            // "BookingPackagesEventBasicDriverRow flex flex-col lg:table-row border-b lg:border-b-0 px-2 pt-3 pb-2 md:w-auto md:px-4 md:py-4 lg:px-0 lg:py-0"
                            "BookingPackagesEventBasicDriverRow flex flex-col lg:table-row border-b-0 px-2 pt-3 pb-2 md:w-auto md:px-4 md:py-4 lg:px-0 lg:py-0"
                        }
                    >
                        <td
                            className={
                                "align-middle py-2 lg:pr-2 lg:py-1 w-full lg:w-auto inline-flex flex-col md:flex-row items-center lg:table-cell border-b border-dashed md:border-b-0"
                            }
                        >
                            <div className={"flex flex-row items-center justify-start mb-2 md:mb-0"}>
                                <UserGroupIcon
                                    className={clsx("h-7 w-7 xl:h-8 xl:w-8 flex-none mr-2", "text-teal-500")} />
                                <div className={"flex flex-col items-center"}>
                                    <span className={"font-bold text-sm xl:text-base"}>{t("Adults")}</span>
                                </div>
                            </div>
                        </td>
                        <td
                            className={"align-middle py-2 lg:px-2 lg:py-1 w-full lg:w-auto inline-flex flex-col md:flex-row md: items-center lg:table-cell"}
                        >
                            <h4 className={"lg:hidden font-bold text-base text-black mb-2 md:mb-0 md:mr-4 shrink-0 md:min-w-[210px]"}>
                                {t("Ticket by hours")}
                                <span>:</span>
                            </h4>
                            <BookingPackageItemsWithKartingCount
                                className={"flex-row items-center justify-center md:justify-start lg:justify-center"}
                                karting={kartingAdults}
                                personCount={personsCountAdult}
                                setKarting={setKartingAdults}
                            />
                        </td>
                        <td className={"align-middle py-2 lg:px-2 lg:py-1 w-full lg:w-auto inline-flex flex-col md:flex-row items-center lg:table-cell"}>
                            <h4 className={"lg:hidden font-bold text-base text-black mb-2 md:mb-0 md:mr-4 shrink-0 md:min-w-[210px]"}>
                                {t("Select visit duration")}
                                <span>:</span>
                            </h4>
                            <BookingPackageItemsDurations
                                disabled1Hour={true}
                                disabled2Hours={false}
                                chosenDuration={chosenDuration}
                                className={"justify-start sm:justify-center md:justify-start lg:justify-center"}
                                enabled={true}
                                personType={PackageItemPersonType.Adult}
                                setChosenDuration={setChosenDuration}
                            />
                        </td>
                        <td
                            className={
                                "align-middle py-2 lg:pl-2 lg:py-1 w-full lg:w-auto inline-flex flex-col md:flex-row items-center text-right lg:table-cell"
                            }
                        >
                            <h4 className={"text-left lg:hidden font-bold text-base text-black mb-2 md:mb-0 md:mr-4 shrink-0 md:min-w-[210px]"}>
                                {t("Price")}
                                <span>:</span>
                            </h4>
                            <div
                                className={clsx(
                                    "flex flex-row items-center justify-end font-medium text-gray-700 text-sm xl:text-base",
                                    totalPriceAdult && "underline underline-offset-4 lg:underline-offset-8",
                                )}
                            >
                                {totalPriceAdult ? <span>{currencyNumberFormat(totalPriceAdult)}</span> :
                                    <span>---</span>}
                            </div>
                        </td>
                    </tr>

                    <tr
                        className={
                            // "BookingPackagesEventBasicDriverRow flex flex-col lg:table-row border-b lg:border-b-0 px-2 pt-3 pb-2 md:w-auto md:px-4 md:py-4 lg:px-0 lg:py-0"
                            "BookingPackagesEventBasicDriverRow flex flex-col lg:table-row border-b-0 px-2 pt-3 pb-2 md:w-auto md:px-4 md:py-4 lg:px-0 lg:py-0"
                        }
                    >
                        <td
                            className={
                                "align-middle py-2 lg:pr-2 lg:py-1 w-full lg:w-auto inline-flex flex-col md:flex-row items-center lg:table-cell border-b border-dashed md:border-b-0"
                            }
                        >
                            <div className={"flex flex-row items-center justify-start mb-2 md:mb-0"}>
                                <UserGroupIcon
                                    className={clsx("h-7 w-7 xl:h-8 xl:w-8 flex-none mr-2", "text-pink-400")} />
                                <div className={"flex flex-col items-center"}>
                                    <span className={"font-bold text-sm xl:text-base"}>{t("Children")}</span>
                                </div>
                            </div>
                        </td>
                        <td
                            className={"align-middle py-2 lg:px-2 lg:py-1 w-full lg:w-auto inline-flex flex-col md:flex-row md: items-center lg:table-cell"}
                        >
                            <h4 className={"lg:hidden font-bold text-base text-black mb-2 md:mb-0 md:mr-4 shrink-0 md:min-w-[210px]"}>
                                {t("Ticket by hours")}
                                <span>:</span>
                            </h4>
                            <BookingPackageItemsWithKartingCount
                                className={"flex-row items-center justify-center md:justify-start lg:justify-center"}
                                karting={kartingChildren}
                                personCount={personsCountChild}
                                setKarting={setKartingChildren}
                            />
                        </td>
                        <td className={"align-middle py-2 lg:px-2 lg:py-1 w-full lg:w-auto inline-flex flex-col md:flex-row items-center lg:table-cell"}>
                            <h4 className={"lg:hidden font-bold text-base text-black mb-2 md:mb-0 md:mr-4 shrink-0 md:min-w-[210px]"}>
                                {t("Select visit duration")}
                                <span>:</span>
                            </h4>
                            <BookingPackageItemsDurations
                                disabled1Hour={true}
                                disabled2Hours={false}
                                chosenDuration={chosenDuration}
                                className={"justify-start sm:justify-center md:justify-start lg:justify-center"}
                                enabled={true}
                                personType={PackageItemPersonType.Child}
                                setChosenDuration={setChosenDuration}
                                isSeller={true}
                            />
                        </td>
                        <td
                            className={
                                "align-middle py-2 lg:pl-2 lg:py-1 w-full lg:w-auto inline-flex flex-col md:flex-row items-center text-right lg:table-cell"
                            }
                        >
                            <h4 className={"text-left lg:hidden font-bold text-base text-black mb-2 md:mb-0 md:mr-4 shrink-0 md:min-w-[210px]"}>
                                {t("Price")}
                                <span>:</span>
                            </h4>
                            <div
                                className={clsx(
                                    "flex flex-row items-center justify-end font-medium text-gray-700 text-sm xl:text-base",
                                    totalPriceChildren && "underline underline-offset-4 lg:underline-offset-8",
                                )}
                            >
                                {totalPriceChildren ? <span>{currencyNumberFormat(totalPriceChildren)}</span> :
                                    <span>---</span>}
                            </div>
                        </td>
                    </tr>
                    </tbody>
                </table>
            </div>

            <div className={"flex flex-col items-end justify-center w-full mt-3 lg:mt-4"}>
                <div
                    className={
                        "flex flex-col items-center justify-center rounded-md bg-white text-sm font-semibold border shadow w-full md:min-w-[210px] md:w-auto"
                    }
                >
                    <h3 className={"font-bold text-xl text-black w-full px-4 py-3 border-b border-dashed flex justify-center md:justify-end"}>
                        {t("Total price")}
                    </h3>
                    <div
                        className={"text-base font-bold text-gray-600 w-full px-4 py-3 flex justify-center md:justify-end"}>
                        {totalPrice ? <span>{currencyNumberFormat(totalPrice)}</span> : <span>---</span>}
                    </div>
                    <div className={"flex flex-col text-center md:text-right text-xs text-gray-500 w-full px-4 pb-3"}>
                        {defaultPackageWithoutKartingAdults?.price && (
                            <span>
                                {t("without Karting")}: {currencyNumberFormat(defaultPackageWithoutKartingAdults?.price)} {t("p.P.")}
                            </span>
                        )}
                        {defaultPackageWithKartingAdults?.price && (
                            <span>
                                {t("with Karting")}: {currencyNumberFormat(defaultPackageWithKartingAdults?.price)} {t("p.P.")}
                            </span>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default withRouter(SellerPackagesAction);
